import {HTTP_SERVICE_INSTANCE} from '@/services/http.service';
import {DisguiseRoute} from '@/routetype';
import EmptyRouterTemplate from '@/views/pageskeleton/EmptyRouterTemplate.vue';
import TabNaviEntryTemplate from '@/views/pageskeleton/TabNaviEntryTemplate.vue';
import DataTableTemplate from '@/views/datatable/DataTableTemplate.vue';
import MachineTimelineTemplate from '@/views/timeline/MachineTimelineTemplate.vue';
import MechanicsTimelineTemplate from '@/views/timeline/MechanicsTimelineTemplate.vue';
import FormTemplate from '@/views/formular/FormTemplate.vue';
import OptionsTemplate from '@/views/OptionsTemplate.vue';
import CompositeTemplate from '@/views/CompositeTemplate.vue';
import TimelineTemplateV2 from '@/views/timeline/views/TimelineTemplateV2.vue';
import SubMenuTemplate from '@/views/SubMenuTemplate.vue';
import StructureTemplate from '@/views/StructureTemplate.vue';
import {CompositeKeyType} from '@/interfaces/key.interface';
import BreadcrumbTemplate from '@/views/BreadcrumbTemplate.vue';
import {URL_SERVICE_INSTANCE} from '@/services/url.service';
import {Observable, shareReplay} from 'rxjs';

class NaviService {
    private static readonly API_PATH = '/navi/v0';
    public readonly API_PATH_SUBMENUS = `${NaviService.API_PATH}/submenus`;

    private naviCache: Observable<DisguiseDto.NaviEntryListDto> | undefined;

    public loadNaviCached(force: boolean = false) {
        if (!this.naviCache || force) {
            this.naviCache = new Observable<DisguiseDto.NaviEntryListDto>((observer) => {
                HTTP_SERVICE_INSTANCE.getAxios().get<DisguiseDto.NaviEntryListDto>(NaviService.API_PATH).then((result) => {
                    observer.next(result.data);
                    observer.complete();
                }).catch((error) => {
                    observer.error(error);
                });
            }).pipe(shareReplay(1));
        }

        return this.naviCache;
    }

    public clearNaviCache() {
        this.naviCache = undefined;
    }

    public loadSubNavi(url: string) {
        return HTTP_SERVICE_INSTANCE.getAxios().get<DisguiseDto.NaviEntryListDto>(url);
    }

    public updateFavoriteOrder(entries: DisguiseDto.NaviFavoriteDto[]) {
        return HTTP_SERVICE_INSTANCE.getAxios().put(NaviService.API_PATH + '/favorites', entries);
    }

    public addFavorite(favoriteEntry: DisguiseDto.NaviFavoriteDto) {
        return HTTP_SERVICE_INSTANCE.getAxios().put(NaviService.API_PATH + '/favorite', favoriteEntry);
    }

    public loadAllSubmenuEntries() {
        return HTTP_SERVICE_INSTANCE.getAxios().get<DisguiseDto.NaviSearchResultDto[]>(
            NaviService.API_PATH + '/submenus/all'
        );
    }

    public removeFavorite(favoriteEntry: DisguiseDto.NaviFavoriteDto) {
        return HTTP_SERVICE_INSTANCE.getAxios().delete(NaviService.API_PATH + '/favorite?' + URL_SERVICE_INSTANCE.serialize(favoriteEntry));
    }

    public extractFavoriteList(
      naviEntries: Array<DisguiseDto.NaviEntryDto<DisguiseDto.SimpleNaviTypeDtoUnion>>
    ): Array<DisguiseDto.NaviEntryDto<DisguiseDto.SimpleNaviTypeDtoUnion>> {
        let result: Array<DisguiseDto.NaviEntryDto<DisguiseDto.SimpleNaviTypeDtoUnion>> = [];
        if (naviEntries) {
            result = this.findFavorites(naviEntries).sort((a, b) => {
                return (a.favoriteIndex || 0) - (b.favoriteIndex || 0);
            });
        }
        return result;
    }

    public convertNaviEntriesToDisguiseRoutes(
      naviList: Array<DisguiseDto.NaviEntryDto<DisguiseDto.SimpleNaviTypeDtoUnion>>,
      contextParameter: CompositeKeyType = {},
      depth: number = 0
    ):
      DisguiseRoute[] {
        const result: DisguiseRoute[] = [];
        for (const currentNaviEntry of naviList) {
            result.push(this.convertNaviType(
              currentNaviEntry.naviType as DisguiseDto.SimpleNaviTypeDtoUnion,
              contextParameter,
              depth,
              currentNaviEntry.children,
              currentNaviEntry.name,
              currentNaviEntry.id,
              currentNaviEntry.canBeFavorite,
              currentNaviEntry.reloadOnUserRequest !== undefined ? currentNaviEntry.reloadOnUserRequest : true
            ));
        }
        return result;
    }

    public convertNaviType(
      naviType: DisguiseDto.SimpleNaviTypeDtoUnion,
      contextParameter: CompositeKeyType,
      depth: number,
      children?: Array<DisguiseDto.NaviEntryDto<DisguiseDto.SimpleNaviTypeDtoUnion>>,
      customName?: string,
      customId?: number,
      canBeFavorite?: boolean,
      reloadOnUserRequest?: boolean
    ): DisguiseRoute {
        let result: DisguiseRoute;
        if (naviType) {
            switch (naviType['@type']) {
                case 'COMPOSITE':
                    result = this.convertComposite(
                      naviType,
                      contextParameter,
                      depth,
                      children,
                      customName,
                      customId,
                      canBeFavorite,
                      reloadOnUserRequest
                    );
                    break;
                case 'FOLDER':
                    result = this.convertFolder(
                      naviType,
                      contextParameter,
                      depth,
                      children,
                      customName,
                      customId,
                      canBeFavorite
                    );
                    break;
                case 'DATATABLE':
                    result = this.convertDataTable(
                      naviType,
                      contextParameter,
                      depth,
                      customName,
                      customId,
                      canBeFavorite,
                      reloadOnUserRequest
                    );
                    break;
                case 'TIMELINE':
                    result = this.convertTimeLine(
                      naviType,
                      contextParameter,
                      depth,
                      customName,
                      customId,
                      canBeFavorite
                    );
                    break;
                case 'TIMELINEV2':
                    result = this.convertTimeLineV2(
                      naviType,
                      contextParameter,
                      depth,
                      customName,
                      customId,
                      canBeFavorite,
                      reloadOnUserRequest
                    );
                    break;
                case 'FORMULAR':
                    result = this.convertFormular(
                      naviType,
                      contextParameter,
                      depth,
                      customName,
                      customId,
                      canBeFavorite,
                      reloadOnUserRequest
                    );
                    break;
                case 'SETTINGS':
                    result = this.convertSettings(
                      naviType,
                      contextParameter,
                      depth,
                      customName,
                      customId,
                      canBeFavorite
                    );
                    break;
                case 'QUICK_LINK':
                    result = this.convertQuickLink(
                      naviType,
                      contextParameter,
                      depth,
                      customName,
                      customId,
                      canBeFavorite
                    );
                    break;
                case 'STRUCTURE':
                    result = this.convertStructure(
                      naviType,
                      contextParameter,
                      depth,
                      customName,
                      customId,
                      canBeFavorite
                    );
                    break;
                case 'BREADCRUMB':
                    result = this.convertBreadcrumb(
                      naviType,
                      contextParameter,
                      depth,
                      children,
                      customName,
                      customId,
                      canBeFavorite
                    );
                    break;
                case 'LINK':
                    result = this.convertLink(
                      naviType,
                      contextParameter,
                      depth,
                      customName,
                      customId,
                      canBeFavorite
                    );
                    break;
                default:
                    throw Error('unknown type');
            }
        } else {
            throw Error('unknown type');
        }
        return result;
    }

    private convertQuickLink(
      naviType: DisguiseDto.QuickLinkNaviTypeDto,
      contextParameter: CompositeKeyType,
      depth: number,
      customName?: string,
      customId?: number,
      canBeFavorite?: boolean
    ): DisguiseRoute {
        const id = naviType.linkId || -1;
        return {
            component: SubMenuTemplate,
            path: (depth === 0 ? '/' : '') + naviType?.path || '',
            meta: {
                naviText: customName || naviType.name || '',
                canBeFavorite: canBeFavorite === true,
                depth,
                id,
                quickLink: true,
            },
            props: {
                contextParameter
            }
        };
    }

    private findFavorites(
      naviEntries: Array<DisguiseDto.NaviEntryDto<DisguiseDto.SimpleNaviTypeDtoUnion>>
    ): Array<DisguiseDto.NaviEntryDto<DisguiseDto.SimpleNaviTypeDtoUnion>> {
        let favorites: Array<DisguiseDto.NaviEntryDto<DisguiseDto.SimpleNaviTypeDtoUnion>> = [];
        for (const currentNaviEntry of naviEntries) {
            if (currentNaviEntry.isFavorite) {
                favorites.push({...currentNaviEntry});
            }
            if (currentNaviEntry.children) {
                favorites = favorites.concat(this.findFavorites(currentNaviEntry.children));
            }
        }
        return favorites;
    }


    private convertFolder(
      naviType: DisguiseDto.FolderNaviTypeDto,
      contextParameter: CompositeKeyType,
      depth: number,
      children?: Array<DisguiseDto.NaviEntryDto<DisguiseDto.SimpleNaviTypeDtoUnion>>,
      customName?: string,
      customId?: number,
      canBeFavorite?: boolean
    ): DisguiseRoute {
        let component = EmptyRouterTemplate;
        let convertedChildren;
        const meta: {
            naviText: string;
            depth: number;
            canBeFavorite: boolean;
            id: number;
        } = {
            naviText: customName || naviType.name || '',
            depth,
            canBeFavorite: canBeFavorite === true,
            id: customId || naviType.id || -1,
        };
        const pathToRegexpOptions = {
            end: true
        };

        if (naviType.showChildrenWithinTabs) {
            component = TabNaviEntryTemplate;
            meta.canBeFavorite = true;
            if (children && children.length) {
                convertedChildren = this.convertNaviEntriesToDisguiseRoutes(
                  children,
                  contextParameter,
                  depth + 1
                );
            }
            pathToRegexpOptions.end = false;
        } else {
            if (children && children.length) {
                convertedChildren = this.convertNaviEntriesToDisguiseRoutes(
                  children,
                  contextParameter,
                  depth + 1
                );
            }
        }

        return {
            component,
            path: (depth === 0 ? '/' : '') + naviType.path,
            meta: {
                ...meta,
                children: convertedChildren
            },
            props: {
                children: convertedChildren
            },
            pathToRegexpOptions,
            children: naviType.showChildrenWithinTabs ? undefined : convertedChildren
        };
    }

    private convertDataTable(
      naviType: DisguiseDto.DataTableNaviTypeDto,
      contextParameter: CompositeKeyType,
      depth: number,
      customName?: string,
      customId?: number,
      canBeFavorite?: boolean,
      reloadOnUserRequest?: boolean
    ): DisguiseRoute {
        return {
            component: DataTableTemplate,
            path: (depth === 0 ? '/' : '') + naviType.path,
            meta: {
                naviText: customName || naviType.name || '',
                canBeFavorite: canBeFavorite === true,
                depth,
                id: customId || naviType.id || -1,
                reloadOnUserRequest
            },
            props: {
                templateUrl: naviType.templateUrl,
                contextParameter,
                reloadOnUserRequest
            }
        };
    }

    private convertTimeLine(
      naviType: DisguiseDto.TimelineNaviTypeDto,
      contextParameter: CompositeKeyType,
      depth: number,
      customName?: string,
      customId?: number,
      canBeFavorite?: boolean
    ): DisguiseRoute {
        let component;
        switch (naviType.objective) {
            case 'MACHINE':
                component = MachineTimelineTemplate;
                break;
            case 'MECHANIC':
                component = MechanicsTimelineTemplate;
                break;
            case 'EVENT':
                component = TimelineTemplateV2;
                break;
        }
        return {
            component,
            path: (depth === 0 ? '/' : '') + naviType.path,
            props: {
                endpointUrl: '/mechanics/jobs/v0/events'
            },
            meta: {
                naviText: customName || naviType.name || '',
                canBeFavorite: canBeFavorite === true,
                depth,
                id: customId || naviType.id || -1,
            }
        };
    }

    private convertTimeLineV2(
      naviType: DisguiseDto.TimelineV2NaviTypeDto,
      contextParameter: CompositeKeyType,
      depth: number,
      customName?: string,
      customId?: number,
      canBeFavorite?: boolean,
      reloadOnUserRequest?: boolean
    ): DisguiseRoute {
        return {
            component: TimelineTemplateV2,
            path: (depth === 0 ? '/' : '') + naviType.path || '',
            props: {
                endpointUrl: naviType.endpointUrl,
                reloadOnUserRequest
            },
            meta: {
                naviText: customName || naviType.name || '',
                canBeFavorite: canBeFavorite === true,
                depth,
                id: customId || naviType.id || -1,
                reloadOnUserRequest
            }
        };
    }

    private convertFormular(
      naviType: DisguiseDto.FormularNaviTypeDto,
      contextParameter: CompositeKeyType,
      depth: number,
      customName?: string,
      customId?: number,
      canBeFavorite?: boolean,
      reloadOnUserRequest?: boolean
    ): DisguiseRoute {
        return {
            component: FormTemplate,
            path: (depth === 0 ? '/' : '') + naviType.path,
            meta: {
                canBeFavorite: canBeFavorite === true,
                naviText: customName || naviType.name || '',
                depth,
                id: customId || naviType.id || -1,
                reloadOnUserRequest
            },
            props: {
                create: false,
                title: customName || naviType.name || '',
                primaryKey: contextParameter,
                templateUrl: naviType.templateUrl,
                entityUrl: naviType.entityUrl,
                reloadOnUserRequest
            }
        };
    }

    private convertSettings(
      naviType: DisguiseDto.SettingsNaviTypeDto,
      contextParameter: CompositeKeyType,
      depth: number,
      customName?: string,
      customId?: number,
      canBeFavorite?: boolean
    ): DisguiseRoute {
        return {
            component: OptionsTemplate,
            path: (depth === 0 ? '/' : '') + naviType.path,
            meta: {
                canBeFavorite: canBeFavorite === true,
                naviText: customName || naviType.name || '',
                depth,
                id: customId || naviType.id || -1,
            }
        };
    }

    private convertStructure(
      naviType: DisguiseDto.StructureNaviTypeDto,
      contextParameter: CompositeKeyType,
      depth: number,
      customName?: string,
      customId?: number,
      canBeFavorite?: boolean,
      reloadOnUserRequest?: boolean
    ): DisguiseRoute {
        return {
            component: StructureTemplate,
            path: (depth === 0 ? '/' : '') + naviType?.path || '',
            meta: {
                canBeFavorite: canBeFavorite === true,
                naviText: customName || naviType.name || '',
                depth,
                id: customId || naviType.id || -1,
                reloadOnUserRequest
            },
            props: {
                templateUrl: naviType.templateUrl || '',
                entityUrl: naviType.entityUrl || '',
                reloadOnUserRequest
            }
        };
    }

    private convertLink(
      naviType: DisguiseDto.LinkNaviTypeDto,
      contextParameter: CompositeKeyType,
      depth: number,
      customName?: string,
      customId?: number,
      canBeFavorite?: boolean
    ): DisguiseRoute {
        return {
            component: EmptyRouterTemplate,
            path: (depth === 0 ? '/' : '') + naviType?.path || '',
            meta: {
                canBeFavorite: canBeFavorite === true,
                naviText: customName || naviType.name || '',
                depth,
                id: customId || naviType.id || -1,
            }
        };
    }

    private convertComposite(
      naviType: DisguiseDto.CompositeNaviTypeDto,
      contextParameter: CompositeKeyType,
      depth: number,
      children?: Array<DisguiseDto.NaviEntryDto<DisguiseDto.SimpleNaviTypeDtoUnion>>,
      customName?: string,
      customId?: number,
      canBeFavorite?: boolean,
      reloadOnUserRequest?: boolean
    ): DisguiseRoute {
        const meta: {
            naviText: string;
            depth: number;
            canBeFavorite: boolean;
            id: number;
            reloadOnUserRequest?: boolean;
        } = {
            naviText: customName || naviType.name || '',
            depth,
            canBeFavorite: canBeFavorite === true,
            id: customId || naviType.id || -1,
            reloadOnUserRequest: reloadOnUserRequest === true
        };

        const convertedChildren = this.convertNaviEntriesToDisguiseRoutes(
          children || [],
          contextParameter,
          depth + 1
        );
        return {
            component: CompositeTemplate,
            path: (depth === 0 ? '/' : '') + String(naviType.id),
            meta: {
                ...meta,
                children: convertedChildren
            },
            props: {
                children: convertedChildren
            }
        };
    }

    private convertBreadcrumb(
      naviType: DisguiseDto.BreadcrumbNaviTypeDto,
      contextParameter: CompositeKeyType,
      depth: number,
      children?: Array<DisguiseDto.NaviEntryDto<DisguiseDto.SimpleNaviTypeDtoUnion>>,
      customName?: string,
      customId?: number,
      canBeFavorite?: boolean
    ): DisguiseRoute {
        const meta: {
            naviText: string;
            depth: number;
            canBeFavorite: boolean;
            id: number;
        } = {
            naviText: customName || naviType.name || '',
            depth,
            canBeFavorite: canBeFavorite === true,
            id: customId || naviType.id || -1
        };

        const convertedChildren = this.convertNaviEntriesToDisguiseRoutes(
          children || [],
          contextParameter,
          depth + 1
        );
        return {
            component: BreadcrumbTemplate,
            path: (depth === 0 ? '/' : '') + String(naviType.id),
            meta: {
                ...meta,
                children: convertedChildren
            },
            props: {
                children: convertedChildren
            }
        };
    }
}

const NAVI_SERVICE_INSTANCE = new NaviService();

export {NAVI_SERVICE_INSTANCE};
